import React, { useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  formatPhoneNumber,
  getTimeZoneCity,
  useApiClient,
  useCurrentTime,
  useFetch,
} from '@marageti/z4-lib';
import { Partner, TripMatch } from '@marageti/z4-sdk/lib/travel';
import { TravelerProfile } from '@marageti/z4-sdk/lib/people/TravelerProfile';
import { Template } from '@marageti/z4-sdk/lib/templates/Template';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { useAuthStore } from '../../store';
import TwoWaySmsView from './two-way-sms-view';
import { groupTemplates, capitalizeErrorMessage } from './helpers';

export type TwoWaySmsContainerProps = {
  open: boolean;
  closeDialog: () => void;
  travelerId: string | undefined;
  conversationId: string | undefined;
  tripMatchId: string | undefined;
  onSuccess?: () => void;
};

const TwoWaySmsContainer = ({
  open,
  closeDialog,
  travelerId,
  conversationId,
  tripMatchId,
  onSuccess,
}: TwoWaySmsContainerProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const apiClient = useApiClient();
  const { loggedInAgent } = useAuthStore();
  const partnerId = loggedInAgent?.partnerId;
  const [errorReason, setErrorReason] = useState<string | null>(null);

  const { data: partner } = useFetch<Partner>(
    apiClient.partnersClient.getPartner,
    [partnerId],
    !!partnerId && open,
  );
  const hasPartnerSmsNumber = !!partner?.smsPhoneNumber;

  const { data: tripMatch } = useFetch<TripMatch>(
    apiClient.tripMatchesClient.getTripMatch,
    [tripMatchId],
    !!tripMatchId && open,
  );

  const traveler = useMemo(() => {
    if (!travelerId || !tripMatch?.travelers?.length) return undefined;
    return tripMatch.travelers.find((t) => t.id === travelerId);
  }, [tripMatch?.travelers, travelerId]);

  const { data: travelerProfile } = useFetch<TravelerProfile>(
    apiClient.peopleClient.getTravelerProfile,
    [travelerId],
    !!travelerId && open,
  );

  const { data: templates } = useFetch<Template[]>(
    apiClient.templatesClient.getTemplates,
    [],
    open,
  );

  const [time] = useCurrentTime(traveler?.timeZone?.name);

  const formattedPhone = formatPhoneNumber(
    traveler?.phone?.prefix,
    traveler?.phone?.number,
  ) || '';

  const localTimeLabel = traveler?.timeZone?.name && time
    ? `${time} in ${getTimeZoneCity(traveler.timeZone.name)}`
    : '';

  const zicassoNumber = useMemo(
    () => formatPhoneNumber('', partner?.smsPhoneNumber) || '',
    [partner?.smsPhoneNumber],
  );

  const isOptedOut = travelerProfile?.preferences?.smsOptOut ?? false;
  const groupedTemplates = useMemo(() => groupTemplates(templates), [templates]);

  const handleSubmit = async (message: string) => {
    if (!conversationId) return;
    setErrorReason(null);
    if (!hasPartnerSmsNumber) {
      setErrorReason('Your partner account does not have an SMS number assigned.');
      return;
    }
    if (isOptedOut) {
      setErrorReason('Traveler opted out of SMS.');
      return;
    }
    try {
      await apiClient.conversationsClient.postSMS(conversationId, { body: message });
      enqueueSnackbar('SMS sent.', { variant: 'success' });
      onSuccess?.();
      closeDialog();
    } catch (e) {
      const apiError = e as ApiError;
      const reason = apiError?.message || 'Try again, or reach out in the app.';
      setErrorReason(capitalizeErrorMessage(reason));
    }
  };

  if (!open || !traveler) {
    return null;
  }

  return (
    <TwoWaySmsView
      open={open}
      closeDialog={closeDialog}
      traveler={traveler}
      formattedPhone={formattedPhone}
      localTimeLabel={localTimeLabel}
      zicassoNumber={zicassoNumber}
      hasPartnerSmsNumber={hasPartnerSmsNumber && !isOptedOut}
      templates={groupedTemplates}
      errorReason={errorReason}
      onDismissError={() => setErrorReason(null)}
      onSubmit={handleSubmit}
    />
  );
};

export default TwoWaySmsContainer;
